/**
 * live-preview-tunnel: a live, end-to-end tunnelled preview of a page the run's machine serves on
 * loopback. The page renders on its own preview origin (`p-<label>.<suffix>`, stable per target),
 * in a sandboxed frame; every request, cookie, stream and WebSocket goes through the E2E tunnel to
 * the machine, so writes land on the machine exactly as they would locally. The static snapshot is
 * the fallback whenever the machine is offline or the preview cannot be opened.
 */
import { Radio, RefreshCw, Square } from 'lucide-react'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { Backend } from '../lib/backend.js'
import {
  attachPreviewFrame,
  type OpenedPreview,
  openPreviewSession,
  type PreviewHostConfig,
  previewHostConfig,
} from '../lib/preview/connect.js'

const SUFFIX = import.meta.env.VITE_VIKO_PREVIEW_SUFFIX as string | undefined

/** The build's preview host config (undefined when live previews are not provisioned). */
export const PREVIEW_HOST = previewHostConfig(SUFFIX)

type State =
  | { k: 'opening' }
  | { k: 'live'; session: OpenedPreview }
  | { k: 'share'; port: number; process: string; busy?: boolean; error?: string }
  | { k: 'fallback'; why: string; retry: boolean }

const DENIED: Record<string, string> = {
  'not-loopback': 'Only pages served on this machine’s own loopback address can be previewed live.',
  'not-listening': 'Nothing is listening on that port right now. The dev server may have stopped.',
  'unknown-run': 'The machine no longer knows this run.',
  'bad-url': 'This address cannot be previewed live.',
  disabled: 'Live previews need an unlocked encrypted account on the machine.',
}
const ENDED: Record<string, string> = {
  offline: 'The machine is offline. Showing the last snapshot.',
  revoked: 'The live preview was stopped on the machine.',
  violation: 'The live preview was closed because a frame failed verification.',
  cap: 'The live preview reached its transfer limit.',
  closed: 'The live preview was closed.',
}

function writesGoTo(platform: string | null | undefined, label: string): string {
  if (platform === 'darwin' || platform === 'macos') return 'the Mac'
  if (platform === 'win32' || platform === 'windows') return 'the PC'
  return label
}

export function LivePreview({
  backend,
  machineId,
  runId,
  projectId,
  url,
  title,
  platform,
  online,
  snapshot,
  host = PREVIEW_HOST,
}: {
  backend: Pick<Backend, 'dispatch' | 'hub' | 'artifactAccess'>
  machineId: string
  runId?: string | undefined
  projectId?: string | undefined
  url: string
  title: string
  platform?: string | null | undefined
  online: boolean
  snapshot: () => ReactNode
  host?: PreviewHostConfig | undefined
}) {
  const [state, setState] = useState<State>({ k: 'opening' })
  const [path, setPath] = useState<string>('')
  const frame = useRef<HTMLIFrameElement>(null)
  const link = useRef<ReturnType<typeof attachPreviewFrame> | undefined>(undefined)
  const live = useRef<OpenedPreview | undefined>(undefined)
  const [attempt, setAttempt] = useState(0)

  const open = useCallback(async () => {
    if (!host) throw new Error('not provisioned')
    return openPreviewSession({
      url,
      ...(runId ? { runId } : {}),
      ...(projectId ? { projectId } : {}),
      accountId: backend.artifactAccess.accountId ?? '',
      machineId,
      host,
      open: async (payload) => (await backend.dispatch('preview.open', machineId, payload)).result,
    })
  }, [backend, host, machineId, projectId, runId, url])

  // Open (or reopen) a session.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` forces a reopen
  useEffect(() => {
    if (!host)
      return setState({
        k: 'fallback',
        why: 'Live previews are not set up for this account.',
        retry: false,
      })
    if (!online) return setState({ k: 'fallback', why: ENDED.offline as string, retry: true })
    if (!backend.artifactAccess.accountId)
      return setState({ k: 'fallback', why: DENIED.disabled as string, retry: false })
    let cancelled = false
    setState({ k: 'opening' })
    open().then(
      (r) => {
        if (cancelled) return
        if (r.status === 'open') {
          live.current = r.session
          setState({ k: 'live', session: r.session })
        } else if (r.status === 'needs_share')
          setState({ k: 'share', port: r.port, process: r.process })
        else
          setState({
            k: 'fallback',
            why: DENIED[r.reason] ?? (DENIED['bad-url'] as string),
            retry: true,
          })
      },
      (e: Error) => {
        if (!cancelled)
          setState({ k: 'fallback', why: e.message || 'The machine did not answer.', retry: true })
      },
    )
    return () => {
      cancelled = true
    }
  }, [open, online, attempt, host, backend])

  // Close the session on the machine when the viewer goes away.
  useEffect(
    () => () => {
      const s = live.current
      if (s)
        void backend
          .dispatch('preview.close', machineId, { previewId: s.previewId })
          .catch(() => {})
    },
    [backend, machineId],
  )

  const session = state.k === 'live' ? state.session : undefined
  const origin = session?.origin
  // Attach the frame once per preview origin; renewals re-key the same frame.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed by origin, re-keyed below
  useEffect(() => {
    const iframe = frame.current
    if (!session || !iframe) return
    const l = attachPreviewFrame({
      iframe,
      session,
      machineId,
      relay: {
        send: (f) => backend.hub.sendPreviewFrame(f),
        subscribe: (cb) => backend.hub.onPreviewFrame(cb),
      },
      onEvent: (e) => {
        if (e.type === 'location') setPath(e.path)
        else if (e.type === 'ended') {
          if (e.reason === 'expired' || e.reason === 'idle' || e.reason === 'replaced') {
            // Renew quietly: same origin (storage, cookies), fresh keys.
            void open().then(
              (r) => {
                if (r.status !== 'open') return setAttempt((n) => n + 1)
                live.current = r.session
                link.current?.rekey(r.session)
              },
              () => setState({ k: 'fallback', why: ENDED.offline as string, retry: true }),
            )
          } else
            setState({
              k: 'fallback',
              why: ENDED[e.reason] ?? (ENDED.closed as string),
              retry: true,
            })
        }
      },
    })
    link.current = l
    return () => l.detach()
  }, [origin])

  const share = async (port: number) => {
    setState((s) => (s.k === 'share' ? ({ ...s, busy: true, error: undefined } as State) : s))
    try {
      await backend.dispatch('preview.share_port', machineId, {
        port,
        ...(projectId ? { projectId } : {}),
      })
      setAttempt((n) => n + 1)
    } catch (e) {
      setState((s) => (s.k === 'share' ? { ...s, busy: false, error: (e as Error).message } : s))
    }
  }

  if (state.k === 'fallback')
    return (
      <div className="vk-livepreview">
        <div className="vk-livepreview__bar" role="status">
          <span className="vk-refpreview__label">{state.why}</span>
          {state.retry ? (
            <button
              type="button"
              className="vk-btn vk-btn--secondary vk-btn--sm"
              onClick={() => setAttempt((n) => n + 1)}
            >
              <RefreshCw size={14} aria-hidden /> Try live again
            </button>
          ) : null}
        </div>
        {snapshot()}
      </div>
    )
  if (state.k === 'share')
    return (
      <div className="vk-livepreview" role="group" aria-label="Share a port">
        <p>
          Port <strong>{state.port}</strong> is served by{' '}
          <code className="vk-md-code">{state.process || 'another process'}</code>, not by this run.
          Share it with your devices? Pages you open can then read and write through it, on the
          machine. You can stop sharing any time.
        </p>
        <div className="vk-livepreview__bar">
          <button
            type="button"
            className="vk-btn vk-btn--primary vk-btn--sm"
            disabled={state.busy}
            onClick={() => void share(state.port)}
          >
            Share port {state.port}
          </button>
          <button
            type="button"
            className="vk-btn vk-btn--secondary vk-btn--sm"
            onClick={() =>
              setState({ k: 'fallback', why: 'Showing the snapshot instead.', retry: true })
            }
          >
            Use the snapshot
          </button>
        </div>
        {state.error ? <p role="alert">{state.error}</p> : null}
      </div>
    )
  if (state.k === 'opening')
    return (
      <div className="vk-livepreview" role="status">
        <span className="vk-refpreview__label">Connecting to the machine…</span>
      </div>
    )
  const s = state.session
  return (
    <div className="vk-livepreview">
      <div className="vk-livepreview__bar">
        <span
          className="vk-pill vk-pill--sm vk-tone-wait"
          title="Requests go end to end encrypted to the machine; the hub sees only sizes and timing."
        >
          <Radio size={12} aria-hidden /> Live on {s.machineLabel} · writes go to{' '}
          {writesGoTo(platform, s.machineLabel)}
        </span>
        <span className="vk-livepreview__path">{path || s.entry}</span>
        <span className="vk-refinspect__spacer" />
        <button
          type="button"
          className="vk-btn vk-btn--secondary vk-btn--sm"
          onClick={() =>
            frame.current?.contentWindow?.postMessage(
              { type: 'viko-preview-go', path: path || s.entry },
              s.origin,
            )
          }
        >
          <RefreshCw size={14} aria-hidden /> Reload
        </button>
        <button
          type="button"
          className="vk-btn vk-btn--secondary vk-btn--sm"
          onClick={() => {
            void backend
              .dispatch('preview.close', machineId, { previewId: s.previewId })
              .catch(() => {})
            live.current = undefined
            setState({
              k: 'fallback',
              why: 'Live preview stopped. Showing the snapshot.',
              retry: true,
            })
          }}
        >
          <Square size={14} aria-hidden /> Stop
        </button>
      </div>
      <iframe
        ref={frame}
        className="vk-livepreview__frame"
        title={title}
        src={`${s.origin}/__viko/host`}
        // The page runs scripts and forms on its own origin only; no popups, top navigation or
        // access to the app. Its keys decrypt only this preview's tunnel.
        sandbox="allow-scripts allow-forms allow-same-origin"
        referrerPolicy="no-referrer"
        allow=""
      />
    </div>
  )
}
